import * as React from "react";
import { useCallback, useEffect, useContext, useMemo, useRef } from "react";
import styled, { css } from "styled-components";
import PageContentLayout from "components/layout/PageContentLayout";
import Sidebar from "views/components/sidebar/Sidebar";
import SearchResult from "views/components/SearchResult";
import { StreamsActions } from "views/stores/StreamsStore";
import HeaderElements from "views/components/HeaderElements";
import QueryBarElements from "views/components/QueryBarElements";
import WindowLeaveMessage from "views/components/common/WindowLeaveMessage";
import IfDashboard from "views/components/dashboard/IfDashboard";
import QueryBar from "views/components/QueryBar";
import { FieldsOverview } from "views/components/sidebar";
import DashboardSearchBar from "views/components/DashboardSearchBar";
import SearchBar from "views/components/SearchBar";
import IfSearch from "views/components/search/IfSearch";
import IfInteractive from "views/components/dashboard/IfInteractive";
import HighlightMessageInQuery from "views/components/messagelist/HighlightMessageInQuery";
import { AdditionalContext } from "views/logic/ActionContext";
import DefaultFieldTypesProvider from "views/components/contexts/DefaultFieldTypesProvider";
import InteractiveContext from "views/components/contexts/InteractiveContext";
import useSearchPageLayout from "hooks/useSearchPageLayout";
import HighlightingRulesProvider from "views/components/contexts/HighlightingRulesProvider";
import SearchExplainContextProvider from "views/components/contexts/SearchExplainContextProvider";
import SearchPagePreferencesProvider from "views/components/contexts/SearchPagePreferencesProvider";
import WidgetFocusProvider from "views/components/contexts/WidgetFocusProvider";
import WidgetFocusContext from "views/components/contexts/WidgetFocusContext";
import useCurrentUser from "hooks/useCurrentUser";
import SynchronizeUrl from "views/components/SynchronizeUrl";
import useView from "views/hooks/useView";
import useViewsDispatch from "views/stores/useViewsDispatch";
import { cancelExecutedJob } from "views/logic/slices/searchExecutionSlice";
import { selectCurrentQueryResults } from "views/logic/slices/viewSelectors";
import useViewsSelector from "views/stores/useViewsSelector";
import useParameters from "views/hooks/useParameters";
import useSearchConfiguration from "hooks/useSearchConfiguration";
import useViewTitle from "views/hooks/useViewTitle";
import { executeActiveQuery } from "views/logic/slices/viewSlice";
import AsideElements from "views/components/AsideElements";
import ExternalValueActionsProvider from "./ExternalValueActionsProvider";
import ViewsFieldActionsProvider from "./ViewsFieldActionsProvider";
const GridContainer = styled.div(
  ({ $interactive }) => $interactive ? css`
        display: flex;
        overflow: auto;
        height: 100%;

        > *:nth-child(2) {
          flex-grow: 1;
        }
      ` : css`
        flex: 1;
      `
);
const SearchArea = styled(PageContentLayout)(() => {
  const { focusedWidget } = useContext(WidgetFocusContext);
  return css`
    ${(focusedWidget == null ? void 0 : focusedWidget.id) && css`
      .page-content-grid {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%;

        /* overflow auto is required to display the message table widget height correctly */
        overflow: ${(focusedWidget == null ? void 0 : focusedWidget.id) ? "auto" : "visible"};
      }
    `}
  `;
});
const ConnectedSidebar = (props) => {
  var _a, _b, _c, _d, _e;
  const results = useViewsSelector(selectCurrentQueryResults);
  const searchPageLayout = useSearchPageLayout();
  const viewTitle = useViewTitle();
  const title = (_b = (_a = searchPageLayout == null ? void 0 : searchPageLayout.sidebar) == null ? void 0 : _a.title) != null ? _b : viewTitle;
  return /* @__PURE__ */ React.createElement(
    Sidebar,
    {
      results,
      title,
      sections: (_c = searchPageLayout == null ? void 0 : searchPageLayout.sidebar) == null ? void 0 : _c.sections,
      contentColumnWidth: (_d = searchPageLayout == null ? void 0 : searchPageLayout.sidebar) == null ? void 0 : _d.contentColumnWidth,
      initialSectionCollapsed: (_e = searchPageLayout == null ? void 0 : searchPageLayout.sidebar) == null ? void 0 : _e.initialSectionCollapsed,
      ...props
    }
  );
};
const ViewAdditionalContextProvider = ({ children }) => {
  const view = useView();
  const { config: searchesClusterConfig } = useSearchConfiguration();
  const { parameters, parameterBindings } = useParameters();
  const currentUser = useCurrentUser();
  const contextValue = useMemo(
    () => ({
      view,
      analysisDisabledFields: searchesClusterConfig == null ? void 0 : searchesClusterConfig.analysis_disabled_fields,
      currentUser,
      parameters,
      parameterBindings
    }),
    [currentUser, parameterBindings, parameters, searchesClusterConfig == null ? void 0 : searchesClusterConfig.analysis_disabled_fields, view]
  );
  return /* @__PURE__ */ React.createElement(AdditionalContext.Provider, { value: contextValue }, children);
};
ViewAdditionalContextProvider.displayName = "ViewAdditionalContextProvider";
const useOnWindowUnload = () => {
  const dispatch = useViewsDispatch();
  return useEffect(() => {
    const handleLeavePage = () => dispatch(cancelExecutedJob());
    window.addEventListener("beforeunload", handleLeavePage);
    return () => {
      handleLeavePage();
      window.removeEventListener("beforeunload", handleLeavePage);
    };
  }, [dispatch]);
};
const Search = ({ forceSideBarPinned = false }) => {
  const dispatch = useViewsDispatch();
  const refreshSearch = useCallback(() => dispatch(executeActiveQuery()), [dispatch]);
  const {
    sidebar: { isShown: showSidebar },
    searchAreaContainer,
    infoBar,
    synchronizeUrl = true
  } = useSearchPageLayout();
  const scrollContainer = useRef(null);
  const InfoBar = infoBar == null ? void 0 : infoBar.component;
  const SearchAreaContainer = searchAreaContainer == null ? void 0 : searchAreaContainer.component;
  const SynchronizationComponent = synchronizeUrl ? SynchronizeUrl : React.Fragment;
  useEffect(() => {
    refreshSearch();
  }, [refreshSearch]);
  useEffect(() => {
    StreamsActions.refresh();
  }, []);
  useOnWindowUnload();
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(SynchronizationComponent, null), /* @__PURE__ */ React.createElement(ViewsFieldActionsProvider, null, /* @__PURE__ */ React.createElement(ExternalValueActionsProvider, null, /* @__PURE__ */ React.createElement(SearchExplainContextProvider, null, /* @__PURE__ */ React.createElement(WidgetFocusProvider, null, /* @__PURE__ */ React.createElement(WidgetFocusContext.Consumer, null, ({
    focusedWidget: { focusing: focusingWidget, editing: editingWidget } = {
      focusing: false,
      editing: false
    }
  }) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(IfInteractive, null, /* @__PURE__ */ React.createElement(IfDashboard, null, /* @__PURE__ */ React.createElement(WindowLeaveMessage, null))), /* @__PURE__ */ React.createElement(InteractiveContext.Consumer, null, (interactive) => /* @__PURE__ */ React.createElement(SearchPagePreferencesProvider, null, /* @__PURE__ */ React.createElement(DefaultFieldTypesProvider, null, /* @__PURE__ */ React.createElement(ViewAdditionalContextProvider, null, /* @__PURE__ */ React.createElement(HighlightingRulesProvider, null, /* @__PURE__ */ React.createElement(GridContainer, { id: "main-row", $interactive: interactive }, /* @__PURE__ */ React.createElement(IfInteractive, null, showSidebar && /* @__PURE__ */ React.createElement(ConnectedSidebar, { forceSideBarPinned }, /* @__PURE__ */ React.createElement(FieldsOverview, null))), /* @__PURE__ */ React.createElement(SearchArea, { as: SearchAreaContainer, ref: scrollContainer }, /* @__PURE__ */ React.createElement(IfInteractive, null, /* @__PURE__ */ React.createElement(HeaderElements, null), InfoBar && /* @__PURE__ */ React.createElement(InfoBar, null), /* @__PURE__ */ React.createElement(IfDashboard, null, !editingWidget && /* @__PURE__ */ React.createElement(DashboardSearchBar, { scrollContainer })), /* @__PURE__ */ React.createElement(IfSearch, null, /* @__PURE__ */ React.createElement(SearchBar, { scrollContainer })), /* @__PURE__ */ React.createElement(QueryBarElements, null), /* @__PURE__ */ React.createElement(IfDashboard, null, !focusingWidget && /* @__PURE__ */ React.createElement(QueryBar, null))), /* @__PURE__ */ React.createElement(HighlightMessageInQuery, null, /* @__PURE__ */ React.createElement(SearchResult, null))), /* @__PURE__ */ React.createElement(IfInteractive, null, /* @__PURE__ */ React.createElement(AsideElements, null)))))))))))))));
};
export default Search;
